import type { ReactNode } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { DefaultLink } from "../../common/DefaultLink";
import type { LinkComponentType } from "../../../types/link";
import { Button, getButtonClassName } from "../../base/Button/Button";
import { Logo } from "../../base/Logo";
import { CampaignBanner } from "../../base/CampaignBanner";
import {
  UserProfileMenu,
  UserAvatarButton,
} from "../../common/UserProfileMenu";
import type { BreadcrumbItem } from "../../base/Breadcrumb";
import { NavIcon } from "../../base/HandIcon/NavIcon";
import { announceSignedOutToOtherTabs } from "../../../lib/announce-signed-out";
import {
  buildHeaderNav,
  resolveActiveNav,
  HEADER_SISTER_LINKS,
  attachReferralOnOpen,
  type HeaderNavArt,
  type HeaderNavItem,
  type HeaderNavMenu,
} from "./headerNav";

export interface PageHeaderSearchSuggestion {
  id?: string;
  label: string;
  description?: string;
  meta?: string;
  onSelect?: () => void;
}

/**
 * @deprecated 帯の中の検索欄は 2026-10 に外した (誰も渡していなかった)。
 * 検索は onSearchClick で開く全体の検索窓に寄せた。型だけ互換のために残す
 */
export interface PageHeaderSearchProps {
  placeholder?: string;
  initialValue?: string;
  ariaLabel?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  suggestions?: PageHeaderSearchSuggestion[];
  onSuggestionSelect?: (suggestion: PageHeaderSearchSuggestion) => void;
}

export interface TeamInfo {
  name: string;
  slug: string;
  iconUrl?: string;
}

export interface PageHeaderProps {
  /**
   * ヘッダのメニューが押されたときに呼ばれる。
   *
   * この package は posthog に依存していないので、**計測そのものは持たない。**
   * 送るかどうかと送り先は apps/client 側が決める
   */
  onNavClick?: (detail: { label: string; to: string; tab: string }) => void;
  /** 現在のパス（未指定の場合はwindow.location.pathnameを使用） */
  currentPath?: string;
  /** カスタムLinkコンポーネント（React Router等） */
  LinkComponent?: LinkComponentType;
  /** ログイン中ユーザーのメールアドレス */
  userEmail?: string;
  /** ユーザー名 */
  userName?: string;
  /**
   * 本人かどうかをまだ読んでいる間 true。ログインの釦を出さず、アバターの大きさの空きだけを置く。
   * 渡さないと、ログイン済みの人にも読み終えるまで「ログイン / 新規登録」が一瞬出る (ログインしていない姿の見せ間違い)
   */
  authPending?: boolean;
  /** ユーザーレベル */
  userLevel?: number;
  /** ユーザーランク (C, C+, B, B+, A, A+, S, S+) */
  userRank?: string;
  /** 合計XP */
  userTotalXp?: number;
  /** 次のレベルまでのXP */
  userXpToNextLevel?: number;
  /** 現在のプラン名 */
  currentPlan?: string;
  /** 所属チーム一覧 */
  teams?: TeamInfo[];
  /**
   * ログイン中だけ、帯の右のアバターの左に置く物 (お知らせの鈴)。中身は apps/client が持つ (/luagate の帯と同じ鈴と小窓)。
   * 渡さなければ何も出さない
   */
  notifications?: ReactNode;
  /**
   * ログイン中のアバターの釦の中身。渡すと既定の緑の丸の代わりにこれを描く (/luagate と同じ丸と「レベル N」)。
   * 渡さなければ今までどおりの緑の丸
   */
  avatar?: ReactNode;
  /** LuaGate の受講生だけに渡す、ダッシュボードの行き先。ログイン中に、アバターのメニューと引き出しに入口を 1 つ出す */
  dashboardHref?: string;
  /** 中央部に表示するタイトル */
  title?: string;
  /** タイトル下に表示するサブタイトル */
  subtitle?: string;
  /** タイトル横に配置する追加要素 */
  actions?: ReactNode;
  /** キャンペーンバナーを表示するかどうか */
  showCampaignBanner?: boolean;
  /** パンくずリスト */
  breadcrumbItems?: BreadcrumbItem[];
  /** プロフィールメニューのクリックハンドラ */
  onProfileClick?: () => void;
  /** アカウント設定のクリックハンドラ */
  onSettingsClick?: () => void;
  /** マイプランのクリックハンドラ */
  onPlanClick?: () => void;
  /** 支払い管理のクリックハンドラ（Stripe Customer Portal） */
  onPaymentClick?: () => void;
  /** ヘルプ・サポートのクリックハンドラ */
  onHelpClick?: () => void;
  /** ログアウトのクリックハンドラ */
  onLogoutClick?: () => void;
  /** @deprecated 帯の中の検索欄は外した。渡しても何も出ない */
  searchProps?: PageHeaderSearchProps;
  /** 検索の印を押したときのハンドラ（全体の検索窓を開く）。無ければ印を出さない */
  onSearchClick?: () => void;
  /**
   * 料金プランリンクの行き先。既定はゆめさくの /code/pricing。
   * 料金の面が別の場所にあるアプリはここで渡す
   */
  pricingHref?: string;
  /**
   * 帯の明暗。ログイン後に /luagate で暗い版を選んだ人は "dark" で描く。
   * 頁の本文は明るいままなので、暗くするのは帯 (と開いた棚と引き出し) だけ
   */
  mode?: "light" | "dark";
  /** 上の細い段 (研究所と女性向け) を出さない。受講生が開く公開の頁で使う */
  hideTopStrip?: boolean;
  /** 左の印をゆめさくコードから差し替える (受講生には /luagate と同じ印) */
  brand?: { href: string; logo: ReactNode };
  /**
   * 描く前に端末の覚え (luagate-theme) を読んで帯の明暗を合わせる台本を、帯の頭に入れる。
   * hydrate のあとで読むと、暗い版を選んだ人にも一瞬明るい帯が出る。ログインしている人にだけ渡す
   */
  restoreSavedMode?: boolean;
  /**
   * 帯の中身の左右に足す余白のクラス。帯の地は全幅のまま、中身だけを本文の列にそろえる。
   * 右に 320px の申込みの面を立てる HP (/) が "xl:pr-[352px]" を渡す
   */
  contentInsetClassName?: string;
  /**
   * 「コース一覧」に hover か focus したときに下へ開く窓。渡したときだけ出る。
   * 窓の中身は apps/client が持つ (データも札も shared-ui には無い)。renderPanel は
   * 窓を初めて開いたときに 1 回だけ呼ぶので、中身の取得と画像は開くまで走らない。
   * 出すのは 1100px 以上でマウスなどの hover できる端末だけ。タッチとスマホは今までどおり頁へ送る
   */
  coursesMenu?: CoursesMenuConfig;
  /**
   * 窓 (IT資格、面接対策、転職) と引き出しの項目に添える絵。項目の id ごと。
   * 絵の住所は apps/client の生成物 (名前にハッシュが入る) なので、ここには持たず外から渡す。
   * 渡さない項目は絵を出さず、名前と 1 行だけになる
   */
  navArt?: Readonly<Record<string, HeaderNavArt>>;
  /**
   * 言語の切り替え。その頁に訳がある言語だけを並べる。2 件未満 (自分の言語しか無い) なら渡さないか
   * 空配列のままにする。渡さない頁は今までどおり何も出ない (既存の呼び出し元は一切変わらない)。
   * 名前はその言語の書き方 (English、日本語)。旗の絵は使わない
   */
  languages?: readonly PageHeaderLanguageOption[];
}

export interface PageHeaderLanguageOption {
  /** BCP 47 のような短い符号 (en、ja)。釦の表示はこれの大文字 */
  code: string;
  /** その言語自身の書き方。切り替えの一覧に出す */
  nativeLabel: string;
  /** 今の頁の、この言語での住所 */
  href: string;
  /** 今開いている言語かどうか */
  current: boolean;
}

export interface CoursesMenuConfig {
  /** 窓の中身。close を呼ぶと窓を閉じる (リンクを押したあと) */
  renderPanel: (api: { close: () => void }) => ReactNode;
  /** 窓の横幅のクラス。既定はコースの大きいメガメニュー用の幅。中身が少ない窓はもっと狭く渡す */
  panelWidthClassName?: string;
}

/** 明暗を覚える端末の鍵。/luagate の LuagateThemeProvider と同じ値 */
export const PAGE_HEADER_MODE_STORAGE_KEY = "luagate-theme";

const RESTORE_MODE_SCRIPT = `(function(){try{var s=document.currentScript,h=s&&s.parentElement;if(h&&localStorage.getItem(${JSON.stringify(PAGE_HEADER_MODE_STORAGE_KEY)})==="dark")h.setAttribute("data-ph-mode","dark")}catch(e){}})()`;

/*
帯の中身の左右の端。**本文の左端とそろえる。** 公開の頁で一番多い本文の器は
max-w-6xl (1152px) の中央寄せ (用語辞典の 533 頁、ガイド、ツール)。1440 で 144px、
スマホは本文と同じ 16px。幅の外側に余白を置くので、広い画面では本文と同じ所から始まる
*/
/*
PC の項目 (ナビ、チーム、新規登録) を出し始める幅。1280px (2026-10-24 に 1100px から上げた)。
768px から出していたとき、ログイン前は 768px で右端が 89px はみ出し、ログイン後は 768〜900px で
最大 195px はみ出した。1024px でもログイン後は 34px はみ出す (帯の中身が約 994px、左右の余白が 64px)。
実測で収まる 1058px に余裕を足して 1100px にしていた。

帯に「セキュリティ学習」を足して 6 項目にしたとき、ROW_INNER が max-w-6xl (1152px) で頭打ちになる
ため、**ビューポートをいくら広げても 1152px より中身は増えない。** 強制的に nowrap させて測ると、
ロゴ+ナビ+右の釦の組の素の幅は 1152px から約 40px はみ出していた (1100px で出していたときの
20px の余裕はそもそも 6 項目には足りなかった)。項目の px (px-3→px-2) とロゴ横の余白
(min-[1280px]:ml-8 の積み増しをやめる) で約 56px 詰め、1152px の中に収めた。
詰めたあとの中身 (約 1128px) が収まる幅は 1152+64=1216px から。そのときの 1280px (下を見る) は 2 本の既存の Tailwind
クラス (min-[1280px]:ml-8 だった箇所) にそろえた切りのいい値で、1216px より少し余裕を持たせてある。

2026-10-30 に帯へ窓「数学・英語」を足して 9 項目になり、自然な幅が約 1227px になって 1280px で右の組が 11px はみ出した。
代表の決め (案 B) で、帯の「コンピュータサイエンス」の表記を「CS」にして約 118px 詰め (HeaderNavItem の barLabel。読み上げの名前、
窓の見出し、引き出しの名前は「コンピュータサイエンス」のまま)、出し始める幅は 1280px のままにした。
一度 1360px に上げたが、この詰めで要らなくなり戻した
2026-11-03 に代表の言葉で「数学・英語」を帯から外し 8 項目に戻った。幅は余るが、出し始める幅は 1280px のまま
この値は下の min-[1280px]: と同じ。変えるときは、この値と min-[1280px]: を全部いっしょに変える
*/
const DRAWER_CLOSE_QUERY = "(min-width: 1280px)";

const ROW_OUTER = "w-full px-4 md:px-8";
const ROW_INNER = "mx-auto w-full max-w-6xl";

/*
焦点の輪。/luagate の帯 (lg.css の .lg-hd__*:focus-visible) と同じ、字の色の 2px を 2px 離して描く。
暗い版では字の色が明るいので、輪も明るくなる
*/
const FOCUS_RING =
  "outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-[var(--color-text-main)] focus:shadow-none!";

/* 印だけの釦 (検索、メニュー)。/luagate の帯の丸と同じ 40px、スマホは押せる 44px */
const ICON_BUTTON = `min-h-11 min-w-11 min-[1280px]:min-h-10 min-[1280px]:min-w-10 p-2! rounded-lg text-main hover:bg-second active:bg-third ${FOCUS_RING}`;

/** 虫眼鏡。前の印は吹き出しに虫眼鏡が重なった形で、検索だと読めなかった */
function SearchGlyph() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

function MenuGlyph({ open }: { open: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      {open ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
    </svg>
  );
}

function ChevronGlyph({ className = "" }: { className?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** 別タブで開くことの印 */
function ExternalTabIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={`h-3 w-3 shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

/** 言語の切り替えの釦。地球の形で、旗は使わない (旗は国の印で言語の印ではない) */
function GlobeGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3Z" />
    </svg>
  );
}

/** 今の言語の印 (チェック) */
function CheckGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L19 7" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-sub" aria-hidden="true">
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


/** 最上段の帯の矢印。内部は右向き、別タブは右上向き。字と同じ色 */
function StripArrow({ external }: { external?: boolean }) {
  return (
    <svg className="ph-strip__arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {external ? <path d="M7 17L17 7M8 7h9v9" /> : <path d="M5 12h14M13 6l6 6-6 6" />}
    </svg>
  );
}

/**
 * 最上段の細い帯。左に研究所、右に女性向け。地は墨 (フッターと同じ)、字は白、項目の間は空白。
 * 文言と行き先は HEADER_SISTER_LINKS。見た目は styles/index.css の .ph-strip が持つ。
 * 暗い版でも同じ墨なので、明暗で変えない (Women のロゴも常に白へ反転)
 */
export function TopStrip({ rowOuter = ROW_OUTER, LinkComponent = DefaultLink }: { rowOuter?: string; LinkComponent?: LinkComponentType }) {
  const [lab, women] = HEADER_SISTER_LINKS;
  return (
    <div className={`${rowOuter} ph-strip`}>
      <div className={`${ROW_INNER} ph-strip__row`}>
        <LinkComponent to={lab.href} className="ph-strip__item">
          {lab.label}
          <StripArrow />
        </LinkComponent>
        <a href={women.href} target="_blank" rel="noopener noreferrer" className="ph-strip__item" onClick={attachReferralOnOpen} onAuxClick={attachReferralOnOpen} onPointerDown={attachReferralOnOpen} onContextMenu={attachReferralOnOpen}>
          <Logo variant="women" className="ph-strip__logo" />
          <span className="ph-strip__label">{women.label}</span>
          <span className="sr-only ph-strip__label-sr">{women.label}</span>
          <StripArrow external />
        </a>
      </div>
    </div>
  );
}

/** ゆめさくコードのロゴ。描き直さない。暗い版は CSS (.ph-logo) が墨と白を入れ替える */
function CodeLogo() {
  return (
    <img
      src="/yumesaku_code.svg"
      alt="ゆめさくコード"
      width={742}
      height={175}
      className="ph-logo block h-7 w-auto min-[1280px]:h-8"
    />
  );
}

function TeamIcon({ team }: { team: TeamInfo }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-main bg-white">
      {team.iconUrl ? (
        <img src={team.iconUrl} alt="" className="h-full w-full object-contain" />
      ) : (
        <span className="max-sm:text-[0.9375rem] text-xs font-bold text-[#14231a]">{team.name.slice(0, 1)}</span>
      )}
    </span>
  );
}

const teamHref = (team: TeamInfo) =>
  team.slug === "luagate" ? "/luagate" : `/teams/${team.slug}`;

/*
PC の帯の 1 項目。/luagate の帯の項目 (.lg-hd__link) と同じ文法で、角 8px (--lg-r-ctl)。
- ふだんは副の字。hover で面の色を敷き、字を本文の色に上げる (地の色だけに頼らない)
- 今いる項目は本文の字と、帯の下の罫に乗せた 2px の緑の線。hover の面は項目の中に収まり、線とは離れているので重ならない
棚 (開く板) は持たない。行き先は 4 本で、板にしまうほどの数が無い
*/
function BarItem({
  item,
  active,
  LinkComponent,
  onNavClick,
  menu,
}: {
  item: HeaderNavItem;
  active: boolean;
  LinkComponent: LinkComponentType;
  onNavClick?: PageHeaderProps["onNavClick"];
  /** 窓を持つ項目だけ渡す。押したときの動きは変えない (行き先へ送る) */
  menu?: { open: boolean; panelId?: string };
}) {
  const className = `relative flex h-10 items-center gap-0.5 whitespace-nowrap rounded-lg px-1 max-sm:text-[0.9375rem] text-sm font-bold no-underline transition-colors hover:bg-second hover:text-main active:bg-third ${active ? "text-main" : "text-sub"} ${FOCUS_RING}`;
  const inner = (
    <>
      {active ? (
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-3 -bottom-2 h-0.5 rounded-full bg-primary" />
      ) : null}
      {item.icon ? <NavIcon name={item.icon} size={16} className="shrink-0 [--ic-mark:var(--color-primary,#05B45B)]" /> : null}
      {item.barLabel ?? item.label}
    </>
  );
  // 別オリジンの絶対 URL は react-router の Link に渡すとパス解決が壊れるので、素の a で同じ窓に開く。
  // セキュリティのように窓 (menu) も持つ項目は、タッチ (hover が無い端末) ではこの a が直に /security/ へ送る側になるので、
  // 窓を持つ内部リンクと同じ aria-expanded・aria-controls・data-ph-courses-trigger を付ける (CoursesBarItem がこれで開閉と Esc を扱う)
  if (item.external) {
    return (
      <a
        href={item.to}
        aria-label={item.barLabel ? item.label : undefined}
        aria-current={active ? "page" : undefined}
        aria-expanded={menu ? menu.open : undefined}
        aria-controls={menu ? menu.panelId : undefined}
        data-ph-courses-trigger={menu ? "" : undefined}
        onClick={() => onNavClick?.({ label: item.label, to: item.to, tab: item.label })}
        className={className}
      >
        {inner}
      </a>
    );
  }
  return (
    <LinkComponent
      to={item.to}
      /* 窓を持つ項目は先読みしない。窓を開かずに通り過ぎただけで、行き先の取得が走らないように */
      prefetch={menu ? "none" : "intent"}
      aria-label={item.barLabel ? item.label : undefined}
      aria-current={active ? "page" : undefined}
      aria-expanded={menu ? menu.open : undefined}
      aria-controls={menu ? menu.panelId : undefined}
      data-ph-courses-trigger={menu ? "" : undefined}
      onClick={() => onNavClick?.({ label: item.label, to: item.to, tab: item.label })}
      className={className}
    >
      {inner}
    </LinkComponent>
  );
}

/*
帯の項目と、その下に開く窓。動きは transitions.dev の 05-menu-dropdown
(.t-dropdown に .is-open を付け外しする。styles/index.css)。
名前は最初に作った「コース一覧」のままだが、仕組みは項目を問わない。2026-10-24 に
「転職」の窓 (転職診断・転職ロードマップ・求人・キャリアの歩き方) でもそのまま使い回した
(0 から作らない。窓の横幅だけ menu.panelWidthClassName で変えられる)。

- hover は 120ms 止まってから開き、外れて 180ms 経ってから閉じる。窓は項目の直下に隙間なく置き、
  マウスが項目から窓へ渡る間に閉じない (隙間が出ても .t-dropdown-slot の橋と、閉じるまでの 180ms が受ける)
- キーボードの focus (focus-visible) でも開く。Esc で閉じ (hover で開いたときも)、焦点が中にあれば項目へ戻す。窓の外へ焦点が出たら閉じる
- マウスとペン以外 (タッチ) では hover でも focus でも開かない。押せば今までどおり頁へ行く
- 窓の中身は最初に開くまで描かない。画像と取得はそこから始まる
- 1100px 未満は帯の項目ごと隠れる (引き出しに回る) ので、窓も出ない
*/
const MENU_OPEN_DELAY_MS = 120;
const MENU_CLOSE_DELAY_MS = 180;

function CoursesBarItem({
  item,
  active,
  LinkComponent,
  onNavClick,
  menu,
  eagerPanel = false,
}: {
  item: HeaderNavItem;
  active: boolean;
  LinkComponent: LinkComponentType;
  onNavClick?: PageHeaderProps["onNavClick"];
  menu: CoursesMenuConfig;
  /**
   * 窓の中身を、開く前から閉じた形で描いておく。apps/lab-astro の書き出し (React を動かさない静的な HTML) は
   * 「初めて開くまで描かない」ための useState の更新が一生起きないので、描いておかないと窓の中身が HTML に
   * 一切入らない。書き出しのビルドだけ (scripts/lab-patches.mjs) が true を渡す。渡すのは中身が静的な一覧の窓
   * (IT資格、セキュリティ、コンピュータサイエンス、面接対策、転職) だけの想定。コース一覧の窓 (CoursesMenuPanel)
   * は開いてから fetch するので、true を渡しても中身は出ず、空の置き場だけが残る
   */
  eagerPanel?: boolean;
}) {
  const panelId = useId();
  const hostRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Esc で項目へ焦点を戻した直後の focus で、開き直さないための印
  const escaped = useRef(false);
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(eagerPanel);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const show = useCallback(() => {
    clearTimer();
    setEverOpened(true);
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, []);
  const later = (fn: () => void, ms: number) => {
    clearTimer();
    timer.current = setTimeout(fn, ms);
  };
  useEffect(() => clearTimer, []);

  const trigger = () => hostRef.current?.querySelector<HTMLElement>("[data-ph-courses-trigger]");

  /*
    Esc は文書全体で受ける。hover で開いた窓は焦点が外 (本文) にあるので、項目の上の keydown では届かない。
    焦点が中にあったときだけ項目へ戻す
  */
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const focusInside = hostRef.current?.contains(document.activeElement) ?? false;
      hide();
      const t = trigger();
      if (focusInside && t && document.activeElement !== t) {
        escaped.current = true;
        t.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, hide]);

  return (
    <div
      ref={hostRef}
      data-ph-menu-host=""
      className="relative flex h-full items-center"
      onPointerEnter={(e) => {
        if (e.pointerType === "touch") return;
        later(show, open ? 0 : MENU_OPEN_DELAY_MS);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "touch") return;
        // キーボードで焦点が中にあるあいだは、ポインターが外れても閉じない (焦点を残したまま窓だけ消えるのを防ぐ)
        const active = document.activeElement;
        if (active instanceof HTMLElement && hostRef.current?.contains(active) && active.matches(":focus-visible")) return;
        // 開く前に外れたら開かない。開いたあとは少し待ってから閉じる
        later(hide, MENU_CLOSE_DELAY_MS);
      }}
      onFocus={(e) => {
        // 押したときの focus では開かない。キーボードで来たときだけ (focus-visible)
        if (escaped.current) {
          escaped.current = false;
          return;
        }
        if (e.target instanceof HTMLElement && e.target.matches(":focus-visible")) show();
      }}
      onBlur={(e) => {
        if (!hostRef.current?.contains(e.relatedTarget as Node | null)) hide();
      }}
    >
      <span data-ph-courses-trigger-wrap="" className="contents">
        <BarItem item={item} active={active} LinkComponent={LinkComponent} onNavClick={onNavClick} menu={{ open, panelId: everOpened ? panelId : undefined }} />
      </span>
      {everOpened ? (
        <div className="t-dropdown-slot absolute left-0 top-full z-50 hidden min-[1280px]:block">
          <div
            id={panelId}
            data-origin="top-left"
            data-ph-courses-panel=""
            aria-hidden={open ? undefined : true}
            className={`t-dropdown ${menu.panelWidthClassName ?? "w-[min(720px,calc(100vw-48px))]"} rounded-xl border border-main bg-surface p-3 text-main shadow-[0_8px_24px_rgba(20,35,26,0.10)]${open ? " is-open" : ""}`}
          >
            {menu.renderPanel({ close: hide })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/*
窓 (IT資格、面接対策、転職) の横幅。絵 (96px) と、名前 (17px) と 1 行 (15px) を折り返さずに置ける幅。
いちばん長い「AWS クラウドプラクティショナー」と、数学の 1 行 (「対数、整数、確率、行列を Python で確かめる」) が
折り返さずに収まることを 1280 と 1440 で測った (2026-10-30 に 440px から 480px へ。数学・英語の窓の 1 行が 440px では枠を越えた)
*/
const NAV_MENU_PANEL_WIDTH = "w-[min(480px,calc(100vw-48px))]";

/*
窓と引き出しの項目の絵。行き先の頁の帯や札と同じ今ある画像を、2 対 1 の枠に切り抜くだけ (描かない)。
暗い版の絵がある物は、帯が暗い版のときに差し替える (styles/index.css の .ph-art--dark。帯の data-ph-mode を見るので、
描く前の台本で暗くした帯でも初めから暗い絵になる)。主役は帯の名前と 1 行なので、絵には墨の枠を付けず細い罫だけにする
*/
function NavArtImage({ art, className }: { art: HeaderNavArt; className: string }) {
  const style = art.position ? { objectPosition: art.position } : undefined;
  const img = "block h-full w-full object-cover";
  return (
    <span aria-hidden="true" className={`relative block shrink-0 overflow-hidden rounded-md border border-main bg-second ${className}`}>
      <img src={art.src} alt="" width={192} height={96} loading="lazy" decoding="async" style={style} className={`${img}${art.darkSrc ? " ph-art--light" : ""}`} />
      {art.darkSrc ? <img src={art.darkSrc} alt="" width={192} height={96} loading="lazy" decoding="async" style={style} className={`${img} ph-art--dark absolute inset-0`} /> : null}
    </span>
  );
}

/*
窓の 1 行。中の項目と下の「すべて見る」で使い回す。external (セキュリティのように別の Worker が出す別アプリ) は
素の a で同じ窓に開く。react-router の Link に別アプリの住所を渡すと、SPA の中の存在しない経路へ飛ぼうとして壊れる
(DrawerItem・BarItem と同じ分け方)
*/
function NavMenuPanelLink({
  item,
  LinkComponent,
  className,
  onClick,
  children,
}: {
  item: HeaderNavItem;
  LinkComponent: LinkComponentType;
  className: string;
  onClick: () => void;
  children: ReactNode;
}) {
  if (item.external) {
    return (
      <a href={item.to} onClick={onClick} className={className}>
        {children}
      </a>
    );
  }
  return (
    <LinkComponent to={item.to} prefetch="none" onClick={onClick} className={className}>
      {children}
    </LinkComponent>
  );
}

/*
窓の中身。コース一覧の窓と同じ CoursesBarItem の窓に入れる。中身は headerNav.ts の menu (静的な一覧) なので取得は無い。
1 行に絵、名前、何の頁かの 1 行。名前と 1 行は折り返さない (窓の幅を NAV_MENU_PANEL_WIDTH で足りる幅にしてある)
*/
function NavMenuPanel({
  menu,
  art,
  LinkComponent,
  onNavigate,
}: {
  menu: HeaderNavMenu;
  art?: Readonly<Record<string, HeaderNavArt>>;
  LinkComponent: LinkComponentType;
  onNavigate: (item: HeaderNavItem) => void;
}) {
  const more = menu.more;
  return (
    <div>
      <ul className="m-0 grid list-none grid-cols-1 gap-0.5 p-0" aria-label={menu.label}>
        {menu.items.map((item) => {
          const itemArt = art?.[item.id];
          return (
            <li key={item.id}>
              <NavMenuPanelLink
                item={item}
                LinkComponent={LinkComponent}
                onClick={() => onNavigate(item)}
                className={`flex min-h-11 items-center gap-3 rounded-lg p-2 text-main no-underline transition-colors hover:bg-second active:bg-third ${FOCUS_RING}`}
              >
                {itemArt ? <NavArtImage art={itemArt} className="h-12 w-24" /> : null}
                <span className="flex min-w-0 flex-col">
                  <span data-ph-menu-label="" className="whitespace-nowrap text-[17px] font-bold leading-snug">
                    {item.label}
                  </span>
                  {item.description ? <span className="whitespace-nowrap text-[15px] leading-snug text-sub">{item.description}</span> : null}
                </span>
              </NavMenuPanelLink>
            </li>
          );
        })}
      </ul>
      {more ? (
        <NavMenuPanelLink
          item={more}
          LinkComponent={LinkComponent}
          onClick={() => onNavigate(more)}
          className={`mt-2 flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-main text-[15px] font-bold text-main no-underline transition-colors hover:bg-second active:bg-third ${FOCUS_RING}`}
        >
          <span data-ph-menu-label="">{more.label}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </NavMenuPanelLink>
      ) : null}
    </div>
  );
}

/*
引き出しの 1 項目。今いる項目は面の色と左の緑の線で示す。
組の見出しの下に 2 列で並べるので、印は付けない (付く項目と付かない項目で左端が揃わない)。字は本文の色にして、副の色の見出しと分ける
*/
function DrawerItem({
  item,
  active,
  art,
  LinkComponent,
  onNavigate,
}: {
  item: HeaderNavItem;
  active: boolean;
  /** 窓を持つ組 (IT資格、面接対策、転職) の項目だけ。あれば 1 行を 1 つで使い、名前の左に小さく置く */
  art?: HeaderNavArt;
  LinkComponent: LinkComponentType;
  onNavigate: (label: string, to: string, tab: string) => void;
}) {
  const className = `relative flex min-h-11 items-center gap-3 rounded-lg px-3 text-[15px] font-bold no-underline transition-colors hover:bg-second hover:text-main active:bg-third ${item.wide || art ? "col-span-2" : ""} ${art ? "py-1" : ""} ${active ? "bg-second text-main" : "text-main"} ${FOCUS_RING}`;
  const inner = (
    <>
      {active ? (
        <span aria-hidden="true" className="pointer-events-none absolute inset-y-2.5 left-0 w-0.5 rounded-full bg-primary" />
      ) : null}
      {art ? <NavArtImage art={art} className="h-7 w-14" /> : null}
      {/* 引き出しは狭い (約 320px) ので、長い名前 (AWS クラウドプラクティショナー) は空白の所でだけ折る。語の途中では折らない */}
      <span className="min-w-0 [word-break:keep-all]">{item.label}</span>
    </>
  );
  // 別オリジンの絶対 URL は react-router の Link に渡すとパス解決が壊れるので、素の a で同じ窓に開く
  if (item.external) {
    return (
      <a href={item.to} aria-current={active ? "page" : undefined} onClick={() => onNavigate(item.label, item.to, item.label)} className={className}>
        {inner}
      </a>
    );
  }
  return (
    <LinkComponent
      to={item.to}
      prefetch="intent"
      aria-current={active ? "page" : undefined}
      onClick={() => onNavigate(item.label, item.to, item.label)}
      className={className}
    >
      {inner}
    </LinkComponent>
  );
}

/**
 * 引き出しを開いた直後、閉じる釦へ焦点を移す。開いた釦が Tab で来ていた (:focus-visible) ときだけ
 * そのまま focus() し、輪を見せる。指で開いたときは data-ph-silent-focus を一瞬だけ付けて輪を隠す
 * (WebKit は script の focus() でも :focus-visible が付く。opener が指で押された直後は focus-visible
 * ではないので、ここで区別できる)。焦点を移す動き自体は両方とも同じ
 */
function focusDrawerClose(closeBtn: HTMLElement | null | undefined, opener: HTMLElement | null) {
  if (!closeBtn) return;
  if (opener?.matches(":focus-visible")) {
    closeBtn.focus();
    return;
  }
  closeBtn.setAttribute("data-ph-silent-focus", "");
  closeBtn.focus({ preventScroll: true });
  requestAnimationFrame(() => closeBtn.removeAttribute("data-ph-silent-focus"));
}

/*
スマホのメニュー。左から出す引き出し (/luagate の学習目次の LgDrawer と同じ動き)。
帯の左の釦から開くので、同じ側から出す。幕を押す、Esc、閉じるの釦で閉じ、開いている間は
Tab が中で巡り、閉じたら開いた釦へ焦点を戻す。LgDrawer は apps/client の部品で
shared-ui からは読めないので、動きだけをここに写している
*/
function Drawer({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const closeBtn = panel?.querySelector<HTMLElement>("[data-ph-drawer-close]");
    focusDrawerClose(closeBtn, opener);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const focusables = [...panel.querySelectorAll<HTMLElement>("a[href], button:not([disabled])")];
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    // 焦点が面の外へ出たら (読み上げの移動、外の要素の focus())、面の頭へ戻す
    const onFocusIn = (e: FocusEvent) => {
      if (panel && e.target instanceof Node && !panel.contains(e.target)) {
        panel.querySelector<HTMLElement>("[data-ph-drawer-close]")?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("focusin", onFocusIn);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", onFocusIn);
      document.body.style.overflow = prevOverflow;
      // 幅が PC になって閉じたときは、開いた釦 (メニューの釦) が隠れている。見えない釦へ焦点を送らない
      const widened =
        typeof window.matchMedia === "function" && window.matchMedia(DRAWER_CLOSE_QUERY).matches;
      if (!widened) opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-start min-[1280px]:hidden">
      <div className="ph-drawer-scrim absolute inset-0 bg-black/60" onMouseDown={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="メニュー"
        data-ph-drawer=""
        className="ph-drawer relative flex h-full w-[min(320px,86vw)] flex-col border-r border-main bg-surface text-main"
      >
        {children}
      </div>
    </div>
  );
}

/*
PC の帯に出す言語の切り替え。「所属チーム」の釦と同じ、押すと右下に開く窓の文法
(ref + 外を押すか Escape で閉じる)。釦の字は符号の大文字 (EN) に固定して幅を
一定にし、13 言語に増えても帯の幅が言語ごとに動かないようにする。
窓の中は各言語自身の書き方 (English、日本語) で、今の言語はリンクにせず印を付ける
*/
function LanguageSwitcher({
  languages,
  LinkComponent,
}: {
  languages: readonly PageHeaderLanguageOption[];
  LinkComponent: LinkComponentType;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = languages.find((l) => l.current) ?? languages[0];

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setIsOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  return (
    <div className="relative hidden min-[1280px]:block" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        type="button"
        className={`min-h-10! shrink-0 gap-1! whitespace-nowrap px-2.5! text-[15px]! font-bold text-main hover:bg-second ${FOCUS_RING}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={
          current.code === "ja"
            ? `言語を切り替える (現在${current.nativeLabel})`
            : `Switch language (current: ${current.nativeLabel})`
        }
        onClick={() => setIsOpen((v) => !v)}
      >
        <GlobeGlyph />
        {current.code.toUpperCase()}
        <ChevronGlyph className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </Button>
      {isOpen ? (
        <div className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-xl border border-main bg-surface p-2 shadow-[0_6px_16px_rgba(20,35,26,0.06)]">
          {languages.map((lang) =>
            lang.current ? (
              <span
                key={lang.code}
                aria-current="true"
                className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-[15px] font-bold text-main"
              >
                {lang.nativeLabel}
                <CheckGlyph />
              </span>
            ) : (
              <LinkComponent
                key={lang.code}
                to={lang.href}
                lang={lang.code}
                hrefLang={lang.code}
                onClick={() => setIsOpen(false)}
                className={`flex items-center rounded-lg px-3 py-2 text-[15px] font-bold text-main no-underline hover:bg-second active:bg-third ${FOCUS_RING}`}
              >
                {lang.nativeLabel}
              </LinkComponent>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

/* スマホの引き出しに出す言語の切り替え。開閉の窓にはせず、言語をそのまま並べる (引き出し自体がもう一覧なので) */
function DrawerLanguageSection({
  languages,
  LinkComponent,
  onClose,
}: {
  languages: readonly PageHeaderLanguageOption[];
  LinkComponent: LinkComponentType;
  onClose: () => void;
}) {
  return (
    <section className="border-b border-main p-2" aria-label="言語の切り替え">
      <p className="px-3 pb-1 pt-2 text-[15px] font-bold text-sub">言語 / Language</p>
      <div className="flex flex-col">
        {languages.map((lang) =>
          lang.current ? (
            <span
              key={lang.code}
              aria-current="true"
              className="flex min-h-11 items-center justify-between gap-2 rounded-lg px-3 text-[15px] font-bold text-main"
            >
              {lang.nativeLabel}
              <CheckGlyph />
            </span>
          ) : (
            <LinkComponent
              key={lang.code}
              to={lang.href}
              lang={lang.code}
              hrefLang={lang.code}
              onClick={onClose}
              className="flex min-h-11 items-center rounded-lg px-3 text-[15px] font-bold text-main no-underline hover:bg-second active:bg-third"
            >
              {lang.nativeLabel}
            </LinkComponent>
          ),
        )}
      </div>
    </section>
  );
}

export function PageHeader({
  onNavClick,
  currentPath,
  LinkComponent = DefaultLink,
  userEmail,
  userName,
  authPending = false,
  userLevel,
  userRank,
  userTotalXp,
  userXpToNextLevel,
  currentPlan,
  teams = [],
  notifications,
  avatar,
  dashboardHref,
  title,
  subtitle,
  actions,
  // 最上部の告知帯は 2026-10-07 に持ち主の指示で既定で出さなくした。出すときは true を渡す
  showCampaignBanner = false,
  breadcrumbItems,
  onPlanClick,
  onPaymentClick,
  onHelpClick,
  onLogoutClick,
  onSearchClick,
  pricingHref = "/code/pricing",
  mode = "light",
  restoreSavedMode = false,
  hideTopStrip = false,
  brand,
  contentInsetClassName = "",
  coursesMenu,
  navArt,
  languages = [],
}: PageHeaderProps) {
  const rowOuter = `${ROW_OUTER} ${contentInsetClassName}`;
  const [isBannerVisible, setIsBannerVisible] = useState(showCampaignBanner);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isTeamMenuOpen, setIsTeamMenuOpen] = useState(false);
  const teamMenuRef = useRef<HTMLDivElement>(null);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  /*
    引き出しを開いたまま幅を広げると、引き出しは min-[1280px]:hidden で見えなくなるが、開いた状態は残る。
    残ると body のスクロールを止め、焦点を見えない面に閉じ込め続ける。PC の幅になったら閉じる
  */
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(DRAWER_CLOSE_QUERY);
    const onChange = () => {
      if (query.matches) setIsDrawerOpen(false);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!isTeamMenuOpen) return;
    const onDown = (event: MouseEvent) => {
      if (teamMenuRef.current && !teamMenuRef.current.contains(event.target as Node)) {
        setIsTeamMenuOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsTeamMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isTeamMenuOpen]);

  const resolvedPath =
    currentPath ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const isLoggedIn = Boolean(userName || userEmail);
  // チームの一覧はログインしている人にだけ出す。一覧だけ届いて本人が無い姿 (所属チームとログインの釦が並ぶ) を作らない
  const hasTeams = isLoggedIn && teams.length > 0;
  const nav = buildHeaderNav({ pricingHref });
  const activeId = resolveActiveNav(resolvedPath, nav, pricingHref);
  const myTeamHref = teams.some((t) => t.slug === "luagate") ? "/luagate" : "/teams";

  const navClick = (label: string, to: string, tab: string) => {
    onNavClick?.({ label, to, tab });
    setIsDrawerOpen(false);
  };

  return (
    <header
      /* 面は bg-surface。bg-white は明るい版でも暗い版でも #fff なので、暗い版で字と重なる。
         どの頁でも上に貼り付ける。z は 40。開いた棚と引き出し (z-50) より下、本文より上 */
      className="ph sticky top-0 z-40 w-full border-b border-main bg-surface text-main"
      data-ph-mode={mode === "dark" ? "dark" : undefined}
      /* 描く前の台本が data-ph-mode を付けるので、hydrate のときの食い違いは帯だけ黙らせる */
      suppressHydrationWarning
    >
      {restoreSavedMode ? (
        <script dangerouslySetInnerHTML={{ __html: RESTORE_MODE_SCRIPT }} />
      ) : null}
      {isBannerVisible && <CampaignBanner onClose={() => setIsBannerVisible(false)} />}

      {/*
        上の細い段。姉妹サイトへの寄り道をここに逃がし、下の段は学習の動線だけに保つ。
        研究所と女性向けの 2 本を、スマホでも名前を落とさずに出す (前は「ゆめさく」が落ちて
        「エンジニア研究所」と読めていた)
      */}
      {/* 上の細い段。姉妹サイトへの寄り道をここに逃がし、下の段は学習の動線だけに保つ。
          研究所と女性向けの 2 本を、スマホでも名前を落とさずに出す */}
      {hideTopStrip ? null : <TopStrip rowOuter={rowOuter} LinkComponent={LinkComponent} />}

      {/* 帯の本体。高さは /luagate の帯と同じ 56px */}
      <div className={rowOuter}>
        <div data-ph-bar="" className={`${ROW_INNER} flex h-14 items-center gap-1`}>
          {/* スマホは /luagate の帯と同じく、左端にメニューの釦。印の左端を本文の左端にそろえる */}
          <Button
            variant="ghost"
            size="sm"
            type="button"
            className={`-ml-2.5 min-[1280px]:hidden ${ICON_BUTTON}`}
            aria-label="メニュー"
            aria-expanded={isDrawerOpen}
            onClick={() => setIsDrawerOpen((v) => !v)}
          >
            <MenuGlyph open={isDrawerOpen} />
          </Button>

          <LinkComponent
            to={brand?.href ?? "/"}
            className={`flex min-h-11 shrink-0 items-center rounded-lg no-underline min-[1280px]:min-h-0 ${FOCUS_RING}`}
          >
            {brand?.logo ?? <CodeLogo />}
          </LinkComponent>

          {/* PC の項目。スマホでも DOM には残す (検索から来る頁の内部リンクを減らさない)。
              2026-10-24、転職診断を単独の項目として足したら 7 項目になり、1152px (ROW_INNER の頭打ち) の
              中で右の釦の組 (ログイン前 224px) が 144px まで押し出されて壊れた。そのあと「転職」を
              CoursesBarItem と同じ窓にまとめ直し (単独リンクでなく転職診断・転職ロードマップ・求人・
              キャリアの歩き方の 4 つを中に入れる窓)、項目の px-2 を px-1.5、アイコンと字の gap-1.5 を
              gap-1、項目どうしの gap-0.5 を 0 に詰めて、1280 / 1366 / 1440 / 1536px で右の組が
              224px (折り返さない) に戻ることを確かめた。

              2026-10-26、セキュリティとコンピュータサイエンスを足して 8 項目になり、右の釦の組
              (検索・ログイン・新規登録) の右端が中身の列 (max-w-6xl、1152px) から約 40px はみ出した。
              表示名を縮めずに詰めるため、項目の px-1.5 を px-1、アイコンと字の gap-1 を gap-0.5、
              帯の中身どうしの gap (ロゴ・ナビ・右の組) を min-[1280px]:gap-2 から常に gap-1 にして、
              自然な幅を 1192px から約 1120px まで詰めた (1152px の列に対し 32px の余裕)。
              1280 / 1366 / 1440px で右の組の右端が列にそろうことを確かめた (details-rubric 的な確認は
              PR の画像を見る)。ロゴとの間を広げる余裕は無いので 1280px になっても ml-6 のまま */}
          <nav aria-label="メインメニュー" className="ml-6 hidden h-full items-center gap-0 min-[1280px]:flex">
            {nav.bar.map((item) => {
              // 窓の入口 (IT資格、面接対策、転職) は resolveActiveNav の候補から外してあり、中の項目の id が返る。
              // 入口も一緒に光らせるので、menu.activeIds を見る。転職は求人とキャリアの歩き方を含めない (住所がまったく別)
              const active = item.menu
                ? activeId === item.id || item.menu.activeIds.has(activeId ?? "")
                : activeId === item.id || (activeId !== null && (item.activeFor?.includes(activeId) ?? false));
              if (item.id === "courses" && coursesMenu) {
                return <CoursesBarItem key={item.id} item={item} active={active} LinkComponent={LinkComponent} onNavClick={onNavClick} menu={coursesMenu} />;
              }
              const menu = item.menu;
              if (menu) {
                return (
                  <CoursesBarItem
                    key={item.id}
                    item={item}
                    active={active}
                    LinkComponent={LinkComponent}
                    onNavClick={onNavClick}
                    menu={{
                      panelWidthClassName: NAV_MENU_PANEL_WIDTH,
                      renderPanel: ({ close }) => (
                        <NavMenuPanel
                          menu={menu}
                          art={navArt}
                          LinkComponent={LinkComponent}
                          onNavigate={(child) => {
                            onNavClick?.({ label: child.label, to: child.to, tab: item.label });
                            close();
                          }}
                        />
                      ),
                    }}
                  />
                );
              }
              return <BarItem key={item.id} item={item} active={active} LinkComponent={LinkComponent} onNavClick={onNavClick} />;
            })}
          </nav>

          <span className="min-w-0 flex-1" />

          <div className="-mr-2.5 flex items-center gap-1 min-[1280px]:mr-0 min-[1280px]:gap-2">
            {onSearchClick ? (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                className={ICON_BUTTON}
                aria-label="検索"
                title="検索"
                onClick={onSearchClick}
              >
                <SearchGlyph />
              </Button>
            ) : null}

            {languages.length >= 2 ? (
              <LanguageSwitcher languages={languages} LinkComponent={LinkComponent} />
            ) : null}

            {hasTeams ? (
              <div className="relative hidden min-[1280px]:block" ref={teamMenuRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  className={`min-h-10! shrink-0 gap-1.5! whitespace-nowrap border border-main px-3! max-sm:text-[0.9375rem] max-sm:text-[0.9375rem]! text-sm! font-bold text-main hover:bg-second ${FOCUS_RING}`}
                  aria-expanded={isTeamMenuOpen}
                  aria-haspopup="true"
                  onClick={() => setIsTeamMenuOpen((prev) => !prev)}
                >
                  所属チーム
                  <ChevronGlyph className={`transition-transform ${isTeamMenuOpen ? "rotate-180" : ""}`} />
                </Button>
                {isTeamMenuOpen ? (
                  <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-main bg-surface p-2 shadow-[0_6px_16px_rgba(20,35,26,0.06)]">
                    {teams.map((team) => (
                      <LinkComponent
                        key={team.slug}
                        to={teamHref(team)}
                        onClick={() => setIsTeamMenuOpen(false)}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2 max-sm:text-[0.9375rem] text-sm font-bold text-main no-underline transition-colors hover:bg-second active:bg-third ${FOCUS_RING}`}
                      >
                        <TeamIcon team={team} />
                        <span data-ph-team-name="" className="min-w-0 line-clamp-2 leading-snug" title={team.name}>
                          {team.name}
                        </span>
                      </LinkComponent>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {isLoggedIn && notifications ? notifications : null}

            {isLoggedIn ? (
              <div className="relative">
                {avatar ? (
                  <button
                    type="button"
                    // 釦の名前が中の札 (レベルの数) を上書きするので、レベルも名前に入れて読み上げる
                    aria-label={userLevel ? `ユーザーメニュー (レベル ${userLevel})` : "ユーザーメニュー"}
                    aria-expanded={isMenuOpen}
                    data-ph-avatar=""
                    className={`flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg px-1 transition-colors hover:bg-second min-[1280px]:min-h-10 ${FOCUS_RING}`}
                    onMouseDown={(e) => {
                      if (isMenuOpen) e.stopPropagation();
                    }}
                    onClick={() => setIsMenuOpen((prev) => !prev)}
                  >
                    {avatar}
                  </button>
                ) : (
                  <UserAvatarButton
                    size="S"
                    className={FOCUS_RING}
                    onMouseDown={(e) => {
                      // 開いている面は mousedown で外を押したと見て閉じる。釦を押して閉じたいときに開き直さない
                      if (isMenuOpen) e.stopPropagation();
                    }}
                    onClick={() => setIsMenuOpen((prev) => !prev)}
                  />
                )}
                <UserProfileMenu
                  isOpen={isMenuOpen}
                  onClose={() => setIsMenuOpen(false)}
                  userName={userName}
                  userLevel={userLevel}
                  userRank={userRank}
                  userTotalXp={userTotalXp}
                  userXpToNextLevel={userXpToNextLevel}
                  currentPlan={currentPlan}
                  LinkComponent={LinkComponent}
                  onPlanClick={onPlanClick}
                  onPaymentClick={onPaymentClick}
                  onSupportClick={onHelpClick}
                  onLogoutClick={onLogoutClick}
                  myTeamHref={myTeamHref}
                  dashboardHref={dashboardHref}
                />
              </div>
            ) : authPending ? (
              /* 読んでいる間はアバターの釦と同じ箱を置く (スマホ 44、PC 40)。cookie のある人だけが読み待ちになり、ほとんどはログイン済みなので、読み終えても帯が動かない */
              <span aria-hidden="true" className="inline-block h-11 w-11 shrink-0 min-[1280px]:h-10 min-[1280px]:w-10" data-testid="page-header-auth-pending" />
            ) : (
              <>
                {/* ログインはスマホでも帯に出す。前はメニューの中にしか無かった */}
                <LinkComponent
                  to="/login"
                  className={`${getButtonClassName({ variant: "ghost", size: "sm" })} min-h-11! min-[1280px]:min-h-10! shrink-0 whitespace-nowrap px-3! max-sm:text-[0.9375rem] max-sm:text-[0.9375rem]! text-sm! text-main! no-underline ${FOCUS_RING}`}
                >
                  {/* 帯が混んでも「ログイ / ン」と語の途中で折らない */}
                  ログイン
                </LinkComponent>
                {/* スマホは帯に入らないので引き出しの頭に置く。hidden を釦の inline-flex と同じ要素に付けると負けるので、外に包む */}
                <span className="hidden min-[1280px]:inline-flex shrink-0">
                  <LinkComponent
                    to="/signup"
                    className={`${getButtonClassName({ variant: "primary", size: "md" })} min-h-10! whitespace-nowrap bg-[var(--color-bg-green-chip,#05b45b)]! px-4! py-1.5! text-[var(--color-text-on-green,#14231a)]! no-underline hover:opacity-90 ${FOCUS_RING}`}
                  >
                    新規登録
                  </LinkComponent>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {isDrawerOpen ? (
        <Drawer onClose={closeDrawer}>
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-main px-4">
            <span className="text-base font-bold text-main">メニュー</span>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              data-ph-drawer-close=""
              className={`-mr-2.5 ${ICON_BUTTON}`}
              aria-label="メニューを閉じる"
              onClick={closeDrawer}
            >
              <MenuGlyph open />
            </Button>
          </div>

          {languages.length >= 2 ? (
            <DrawerLanguageSection languages={languages} LinkComponent={LinkComponent} onClose={closeDrawer} />
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {/*
              組ごとに見出しを付け、項目は 2 列に並べる。1 列だと 12 項目が縦に並び、390 x 844 で料金が 1 画面に入らなかった。
              2 列にしても押せる高さは 44px のまま。字の長い項目 (面接の 2 つ) は 1 行を 1 つで使う
            */}
            <nav aria-label="メインメニュー" className="flex flex-col px-2 pb-2">
              {nav.drawer.map((group, index) => (
                /* 組の間は見出しだけで分け、罫は見出しの無い組 (料金) の前にだけ引く。iPhone の Safari の高さ (390 x 664) で料金まで 1 画面に入れるため */
                <section key={group.id} aria-label={group.label || undefined} className={index === 0 ? "" : group.label ? "mt-0.5" : "mt-1 border-t border-main pt-1"}>
                  {group.label ? (
                    <p className="px-3 pt-1 text-[15px] font-bold leading-6 text-sub">{group.label}</p>
                  ) : null}
                  <div className="grid grid-cols-2 gap-x-1">
                    {group.items.map((item) => (
                      <DrawerItem
                        key={item.id}
                        item={item}
                        active={activeId === item.id}
                        art={navArt?.[item.id]}
                        LinkComponent={LinkComponent}
                        onNavigate={navClick}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </nav>

            {isLoggedIn && hasTeams ? (
              <section className="border-t border-main p-2" aria-label="所属チーム">
                <p className="px-3 pb-1 pt-2 max-sm:text-[0.9375rem] text-xs font-bold text-sub">所属チーム</p>
                {teams.map((team) => (
                  <LinkComponent
                    key={team.slug}
                    to={teamHref(team)}
                    onClick={closeDrawer}
                    className={`flex min-h-11 items-center gap-3 rounded-lg px-3 max-sm:text-[0.9375rem] text-sm font-bold text-main no-underline hover:bg-second active:bg-third ${FOCUS_RING}`}
                  >
                    <TeamIcon team={team} />
                    <span data-ph-team-name="" className="min-w-0 line-clamp-2 leading-snug" title={team.name}>
                      {team.name}
                    </span>
                  </LinkComponent>
                ))}
              </section>
            ) : null}

            <section className="border-t border-main p-2" aria-label="ゆめさくのほかのサイト">
              <p className="px-3 pb-1 pt-2 max-sm:text-[0.9375rem] text-xs font-bold text-sub">ゆめさくのほかのサイト</p>
              {HEADER_SISTER_LINKS.map((link) =>
                link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      attachReferralOnOpen(e);
                      closeDrawer();
                    }}
                    onPointerDown={attachReferralOnOpen}
                    onContextMenu={attachReferralOnOpen}
                    className={`flex min-h-11 flex-col items-start justify-center gap-1 rounded-lg px-3 py-2 max-sm:text-[0.9375rem] text-xs text-sub no-underline hover:bg-second ${FOCUS_RING}`}
                  >
                    {/* ロゴと名前を横に並べると 320px の面で名前が「エンジニア / スクール」と折れる。縦に積む */}
                    <Logo variant="women" className="ph-logo h-6 w-auto" />
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      {link.label}
                      <ExternalTabIcon />
                    </span>
                  </a>
                ) : (
                  <LinkComponent
                    key={link.href}
                    to={link.href}
                    onClick={closeDrawer}
                    className={`flex min-h-11 items-center rounded-lg px-3 max-sm:text-[0.9375rem] text-sm text-main no-underline hover:bg-second ${FOCUS_RING}`}
                  >
                    {link.label}
                  </LinkComponent>
                ),
              )}
            </section>
          </div>

          <div className="shrink-0 border-t border-main p-4">
            {isLoggedIn ? (
              <div className="flex flex-col gap-1">
                {userName ? (
                  <p className="truncate px-3 pb-1 max-sm:text-[0.9375rem] text-sm font-bold text-main" title={userName}>
                    {userName}
                  </p>
                ) : null}
                {/* ホームは帯から外したので、ログイン後の入口はこことアバターのメニュー */}
                <LinkComponent
                  to="/home"
                  onClick={closeDrawer}
                  className={`flex min-h-11 items-center rounded-lg px-3 max-sm:text-[0.9375rem] text-sm font-bold text-main no-underline hover:bg-second ${FOCUS_RING}`}
                >
                  ホーム
                </LinkComponent>
                {dashboardHref ? (
                  <LinkComponent
                    to={dashboardHref}
                    onClick={closeDrawer}
                    className={`flex min-h-11 items-center rounded-lg px-3 max-sm:text-[0.9375rem] text-sm font-bold text-main no-underline hover:bg-second ${FOCUS_RING}`}
                  >
                    ダッシュボード
                  </LinkComponent>
                ) : null}
                <LinkComponent
                  to="/settings/profile"
                  onClick={closeDrawer}
                  className={`flex min-h-11 items-center rounded-lg px-3 max-sm:text-[0.9375rem] text-sm text-main no-underline hover:bg-second ${FOCUS_RING}`}
                >
                  プロフィール
                </LinkComponent>
                <form
                  method="post"
                  action="/api/auth/sign-out"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setIsDrawerOpen(false);
                    try {
                      await fetch("/api/auth/sign-out", { method: "POST", credentials: "include" });
                      announceSignedOutToOtherTabs();
                    } finally {
                      window.location.href = "/login";
                    }
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    type="submit"
                    fullWidth
                    className={`min-h-11! justify-start! px-3! max-sm:text-[0.9375rem] max-sm:text-[0.9375rem]! text-sm! font-normal! text-main! ${FOCUS_RING}`}
                  >
                    ログアウト
                  </Button>
                </form>
              </div>
            ) : authPending ? (
              /* 読んでいる間は、ログイン後の欄 (名前、ホーム、ダッシュボード、プロフィール、ログアウト) と同じ高さの空きを置く。前は何も置かず、読み終えると引き出しの下が伸びた */
              <div aria-hidden="true" className="invisible flex flex-col gap-1" data-testid="page-header-drawer-auth-pending">
                <p className="px-3 pb-1 max-sm:text-[0.9375rem] text-sm font-bold">&nbsp;</p>
                {Array.from({ length: dashboardHref ? 4 : 3 }, (_, i) => (
                  <span key={i} className="block min-h-11" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <LinkComponent
                  to="/signup"
                  onClick={closeDrawer}
                  className={`${getButtonClassName({ variant: "primary", size: "md", fullWidth: true })} bg-[var(--color-bg-green-chip,#05b45b)]! text-[var(--color-text-on-green,#14231a)]! no-underline hover:opacity-90 ${FOCUS_RING}`}
                >
                  新規登録
                </LinkComponent>
                <LinkComponent
                  to="/login"
                  onClick={closeDrawer}
                  className={`${getButtonClassName({ variant: "outlineGray", size: "md", fullWidth: true })} text-main! no-underline ${FOCUS_RING}`}
                >
                  ログイン
                </LinkComponent>
              </div>
            )}
          </div>
        </Drawer>
      ) : null}

      {breadcrumbItems && breadcrumbItems.length > 0 && (
        <nav className={`${rowOuter} pb-2 pt-3`} aria-label="パンくずリスト">
          <ol className={`${ROW_INNER} flex flex-wrap items-center gap-1.5 max-sm:text-[0.9375rem] text-sm`}>
            <li className="flex items-center gap-1.5">
              <LinkComponent to="/" className="text-sub no-underline transition-colors hover:text-primary">
                ホーム
              </LinkComponent>
              <ChevronRightIcon />
            </li>
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              return (
                <li key={index} className="flex items-center gap-1.5">
                  {item.href && !isLast ? (
                    <LinkComponent to={item.href} className="text-sub no-underline transition-colors hover:text-primary">
                      {item.label}
                    </LinkComponent>
                  ) : (
                    <span className="font-medium text-main">{item.label}</span>
                  )}
                  {!isLast && <ChevronRightIcon />}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {(title || subtitle || actions) && (
        <div className={`${rowOuter} pb-4 md:pb-6`}>
          <div className={`${ROW_INNER} flex flex-col gap-2`}>
            <div className="flex items-start justify-between gap-4 md:gap-6">
              {title && <h1 className="text-xl font-bold text-main md:text-2xl">{title}</h1>}
              {actions}
            </div>
            {subtitle && <p className="max-sm:text-[0.9375rem] text-sm text-sub">{subtitle}</p>}
          </div>
        </div>
      )}
    </header>
  );
}
